import { useRequiredContext } from "@redotech/react-util/context";
import UploadCloudIcon from "@redotech/redo-icons/upload-cloud-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoAdminClientContext } from "../../../../../client/context";
import { ImportPlatform } from "../types";
import { useMacroImport } from "../use-macro-import";

interface MacrosTabProps {
  teamId: string | undefined;
  platform: ImportPlatform;
}

const PLATFORM_DISPLAY_NAMES: Record<ImportPlatform, string> = {
  [ImportPlatform.GORGIAS]: "Gorgias",
  [ImportPlatform.ZENDESK]: "Zendesk",
  [ImportPlatform.REAMAZE]: "Reamaze",
  [ImportPlatform.RICHPANEL]: "RichPanel",
};

export const MacrosTab = memo(function MacrosTab({
  teamId,
  platform,
}: MacrosTabProps) {
  const client = useRequiredContext(RedoAdminClientContext);
  const { file, loading, handleFileChange, handleSubmit } = useMacroImport({
    teamId,
    platform,
    client,
  });

  const platformDisplayName = PLATFORM_DISPLAY_NAMES[platform];
  const inputId = `${platform}-macro-upload`;

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" textColor="secondary">
        Upload a CSV of {platformDisplayName} macros. You can upload one file at
        a time.
      </Text>
      <Flex align="center" gap="md">
        <label htmlFor={inputId}>
          <RedoButton
            disabled={loading}
            hierarchy="secondary"
            IconLeading={UploadCloudIcon}
            onClick={() => document.getElementById(inputId)?.click()}
            size="md"
            text="Choose File"
            theme="normal"
          />
        </label>
        <input
          accept=".csv"
          id={inputId}
          onChange={handleFileChange}
          style={{ display: "none" }}
          type="file"
        />
        {file && (
          <Text fontSize="sm" textColor="secondary">
            {file.name}
          </Text>
        )}
      </Flex>
      <div>
        <RedoButton
          disabled={!file || loading}
          hierarchy="primary"
          IconLeading={UploadCloudIcon}
          onClick={handleSubmit}
          pending={loading}
          size="md"
          text="Import Macros"
          theme="normal"
        />
      </div>
    </Flex>
  );
});

export function createMacrosTab(platform: ImportPlatform) {
  return memo(function PlatformMacrosTab({
    teamId,
  }: {
    teamId: string | undefined;
  }) {
    return <MacrosTab platform={platform} teamId={teamId} />;
  });
}
